import isObject from 'lodash/isObject';
import { mapObjToFormData } from '@/shared/utils';
import { OpenapiContentType, OpenapiContext } from './openapiContext';

const baseUrl = `${import.meta.env.VITE_API_BASE_URL}`;

export type ErrorStatus = number | 'unknown';

export type ErrorWrapper<TError> = {
  name: string;
  message: string;
  status: ErrorStatus;
  stack: ErrorResponse<TError>;
};

export type ErrorResponse<TError> =
  | TError
  | { status: ErrorStatus; payload: string };

export type OpenapiFetcherOptions<TBody, THeaders, TQueryParams, TPathParams> =
  {
    url: string;
    method: string;
    body?: TBody;
    headers?: THeaders;
    queryParams?: TQueryParams;
    pathParams?: TPathParams;
    signal?: AbortSignal;
  } & OpenapiContext['fetcherOptions'];

export async function openapiFetch<
  TData,
  TError,
  TBody extends {} | undefined | null,
  THeaders extends {},
  TQueryParams extends {},
  TPathParams extends {},
>({
  url,
  method,
  body,
  contentType,
  headers,
  pathParams,
  queryParams,
  signal,
}: OpenapiFetcherOptions<
  TBody,
  THeaders,
  TQueryParams,
  TPathParams
>): Promise<TData> {
  try {
    const isFormDataBody = contentType === OpenapiContentType.FormData;
    const requestHeaders: HeadersInit = {
      'Content-Type': contentType ?? OpenapiContentType.Json,
      ...headers,
    };

    /**
     * As the fetch API is being used, when FormData is used as the body,
     * the Content-Type header must be deleted so that the browser can set
     * the correct boundary.
     * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
     */
    if (isFormDataBody) {
      delete requestHeaders['Content-Type'];
    }

    const response = await window.fetch(
      `${baseUrl}${resolveUrl(url, queryParams, pathParams)}`,
      {
        signal,
        method: method.toUpperCase(),
        body: body
          ? isFormDataBody
            ? mapObjToFormData(body)
            : JSON.stringify(body)
          : undefined,
        headers: requestHeaders,
        credentials: 'include',
      },
    );
    if (!response.ok) {
      let error: ErrorResponse<TError>;
      try {
        error = await response.json();
      } catch (e) {
        const { message, status } = resolveErrorMessage(e, 'Unexpected error');
        error = { status, payload: message };
      }

      throw error;
    }

    if (response.headers.get('content-type')?.includes('json')) {
      return await response.json();
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      return (await response.blob()) as unknown as TData;
    }
  } catch (e) {
    const error = resolveErrorMessage(e, 'Network error');
    throw {
      name: 'unknown' as const,
      stack: e as string,
      ...error,
    };
  }
}

const resolveErrorMessage = (error: unknown, defaultText: string) => {
  const status = isObject(error) && 'statusCode' in error && error.statusCode;
  const message = isObject(error) && 'message' in error && error.message;
  return {
    message: message ? `${defaultText} (${message})` : defaultText,
    status: (status || 'unknown') as ErrorStatus,
  };
};

const resolveQueryParams = (params: Record<string, string | string[]>) => {
  const query = Object.entries(params).reduce((searchParams, [key, value]) => {
    const arrValue = Array.isArray(value) ? value : [value];
    arrValue.forEach((val) => searchParams.append(key, val));
    return searchParams;
  }, new URLSearchParams());

  return query.toString();
};

const resolveUrl = (
  url: string,
  queryParams: Record<string, string | string[]> = {},
  pathParams: Record<string, string> = {},
) => {
  let query = resolveQueryParams(queryParams);
  if (query) query = `?${query}`;
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query;
};
